import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import {
    fetchVendorById,
    clearVendorState,
} from "../redux/slices/vendorSlice";
import { fetchCategories } from "../redux/slices/categorySlice";


/* ================================
   COMPONENT
================================ */
const EditVendorProfile = () => {
    
    const { vendorId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { vendor, loading, error, successMessage } = useSelector(
        (state) => state.vendor
    );
    const { categories = [] } = useSelector((state) => state.category);

    /* ================================
       LOCAL STATE
    ================================ */
    const [form, setForm] = useState({
        shopName: "",
        ownerName: "",
        mobile: "",
        whatsappNumber: "",
        address: "",
        city: "",
        state: "",
        workExperience: "",
        fromDay: "Monday",
        toDay: "Saturday",
        openTime: "09:00",
        closeTime: "18:00",
        sundayOpen: false,
    });

    const [selectedCategories, setSelectedCategories] = useState([]);
    const [image, setImage] = useState(null);

    /* ================================
       FETCH DATA
    ================================ */
    useEffect(() => {
        dispatch(fetchVendorById(vendorId));
        dispatch(fetchCategories());
    }, [dispatch, vendorId]);

    /* ================================
       SET FORM DATA
    ================================ */
    useEffect(() => {
        if (vendor) {
            setForm({
                shopName: vendor.shopName || "",
                ownerName: vendor.ownerName || "",
                mobile: vendor.mobile || "",
                whatsappNumber: vendor.whatsappNumber || "",
                address: vendor.address || "",
                city: vendor.city || "",
                state: vendor.state || "",
                workExperience: vendor.workExperience || "",
                fromDay: vendor.availability?.fromDay || "Monday",
                toDay: vendor.availability?.toDay || "Saturday",
                openTime: vendor.availability?.openTime || "09:00",
                closeTime: vendor.availability?.closeTime || "18:00",
                sundayOpen: vendor.availability?.sundayOpen || false,
            });

            setSelectedCategories(vendor.category?.map((c) => c._id) || []);
        }
    }, [vendor]);

    /* ================================
       HANDLERS
    ================================ */
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e) => {
        if (e.target.files?.length) {
            setImage(e.target.files[0]);
        }
    };

    const toggleCategory = (id) => {
        setSelectedCategories((prev) =>
            prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
        );
    };

    /* ================================
       UPDATE
    ================================ */
    const handleUpdate = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Auth token missing");

            const formData = new FormData();

            Object.entries(form).forEach(([key, value]) => {
                formData.append(key, value);
            });

            formData.append(
                "availability[fromDay]",
                form.fromDay
            );
            formData.append(
                "availability[toDay]",
                form.toDay
            );
            formData.append(
                "availability[openTime]",
                form.openTime
            );
            formData.append(
                "availability[closeTime]",
                form.closeTime
            );
            formData.append(
                "availability[sundayOpen]",
                form.sundayOpen ? "true" : "false"
            );

            formData.append("category", JSON.stringify(selectedCategories));

            if (image) {
                formData.append("shopImage", image);
            }

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/vendors/update-vendor/${vendorId}`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                }
            );

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data?.message || "Update failed");
            }

            Swal.fire({
                icon: "success",
                title: "Profile Updated",
                text: "Vendor profile updated successfully",
                timer: 2000,
                showConfirmButton: false,
            });

            dispatch(clearVendorState());
            navigate("/my-profile");

        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };

    /* ================================
       UI
    ================================ */
    return (
        <div className="container py-4">
            <div className="card shadow-sm">
                <div className="card-header bg-dark text-white">
                    <h6 className="mb-0">Update Vendor Profile</h6>
                </div>

                <div className="card-body">
                    {/* IMAGE */}
                    <div className="text-center mb-4">
                        <img
                            src={
                                image
                                    ? URL.createObjectURL(image)
                                    : vendor?.shopImage || "/placeholder.png"
                            }
                            alt="shop"
                            className="rounded-circle mb-2"
                            width="120"
                            height="120"
                        />
                        <input
                            type="file"
                            className="form-control form-control-sm"
                            onChange={handleImageChange}
                        />
                    </div>

                    {/* INPUTS */}
                    {[
                        ["Shop Name", "shopName"],
                        ["Owner Name", "ownerName"],
                        ["Mobile", "mobile"],
                        ["WhatsApp Number", "whatsappNumber"],
                        ["Work Experience", "workExperience"],
                        ["Address", "address"],
                        ["City", "city"],
                        ["State", "state"],
                    ].map(([label, name]) => (
                        <div className="mb-3" key={name}>
                            <label className="form-label">{label}</label>
                            <input
                                type="text"
                                className="form-control"
                                name={name}
                                value={form[name]}
                                onChange={handleChange}
                            />
                        </div>
                    ))}

                    {/* CATEGORIES */}
                    <div className="mb-3">
                        <label className="form-label">Select Services</label>
                        <div className="border rounded p-2">
                            {categories.map((cat) => (
                                <div key={cat._id} className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={selectedCategories.includes(cat._id)}
                                        onChange={() => toggleCategory(cat._id)}
                                    />
                                    <label className="form-check-label">
                                        {cat.name}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* BUTTON */}
                    <button
                        className="btn btn-dark w-100"
                        onClick={handleUpdate}
                        disabled={loading}
                    >
                        {loading ? "Updating..." : "Update Profile"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditVendorProfile;
